import React, { useCallback, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, TextInput, Text, Button, H4 } from '@revdotcom/vaporwave';

import { useSubmit } from '../useSubmit';
import { fixupEvents } from '../helpers';
import { type VerifyPasswordPageProps } from '../../codegen/VerifyPasswordPageProps';
import { type VerifyPasswordPageSubmit } from '../../codegen/VerifyPasswordPageSubmit';
import { CardLogo } from '../components/CardLogo';
import { UnderLinks } from '../components/UnderLinks';
import styles from '../../styles';
import { type ForgotPasswordProps } from '../../codegen/ForgotPasswordProps';
import { type FlowProps } from '../../FlowProps';

/**
 * Password verification page in the login flow.
 * @param {VerifyPasswordPageProps} props page parameters
 * @returns {object} form component
 */
const VerifyPasswordPage = (props: FlowProps<VerifyPasswordPageProps>) => {
    // enable react-hook-form form orchestration
    const {
        register,
        handleSubmit,
        setFocus,
        formState: { errors, isSubmitting },
    } = useForm<VerifyPasswordPageSubmit>({
        mode: 'onSubmit',
        criteriaMode: 'all',
        defaultValues: {
            contextId: props.contextId,
        },
    });

    const returnUrl = props.returnUrl;

    // set up our ajax submit handler
    const { onSubmit, submitError, isRedirecting } = useSubmit(
        '/login/verifypassword',
        {
            captchaKey: props.captchaKey,
            antiforgeryToken: props.verificationToken,

            preSubmit: useCallback(
                (data: VerifyPasswordPageSubmit) => {
                    // post back
                    data.contextId = props.contextId;
                },
                [props.contextId],
            ),
        },
    );

    // "show password in clear" flag
    const [showPassword, setShowPassword] = useState(false);

    // auto-focus
    useEffect(() => {
        setFocus('password');
    }, [setFocus]);

    const navigate = useNavigate();

    const onForgotPassword = useCallback(() => {
        const state: ForgotPasswordProps = {
            verificationToken: props.verificationToken,
            viewData: props.viewData,
            captchaKey: props.captchaKey,
            allowPersistent: false,
            emailSent: false,
        };

        const encodedReturnUrl = encodeURIComponent(returnUrl);
        const query = `returnUrl=${encodedReturnUrl}`;
        navigate('/password/forgot?' + query, { state });
    }, [props, navigate, returnUrl]);

    return (
        <Card sx={styles.cardVerifyPassword}>
            <CardLogo {...props} />

            <div sx={styles.cardContainer}>
                <H4 sx={styles.cardTitle}>Verify password</H4>

                <Text sx={styles.verifyPasswordPromptText}>
                    {props.promptMessage}
                </Text>

                <div sx={{ position: 'relative' }}>
                    <TextInput
                        id="email-input"
                        label="Email address"
                        inputMode="email"
                        size="large"
                        type="email"
                        isDisabled
                        sx={{
                            variant: 'forms.identity.verifyPassword.userName',
                        }}
                        value={props.userName}
                    />
                </div>

                <form sx={styles.cardForm} onSubmit={handleSubmit(onSubmit)}>
                    {/* make sure that password field is always present to fool password managers */}
                    <div sx={{ position: 'relative', display: 'block' }}>
                        <TextInput
                            id="password-input"
                            label="Password"
                            autoComplete="current-password"
                            size="large"
                            isRequired
                            type={showPassword ? 'text' : 'password'}
                            validationState={errors.password && 'invalid'}
                            helperText={
                                errors.password && 'Please enter a password.'
                            }
                            sx={{
                                variant:
                                    'forms.identity.verifyPassword.password',
                            }}
                            {...fixupEvents(register('password'))}
                        />

                        <Button
                            id="show-password-button"
                            sizeVariant="minimal"
                            variant="text"
                            onPress={() => setShowPassword((f) => !f)}
                            sx={styles.cardShowPassword}
                        >
                            {showPassword ? 'Hide' : 'Show'}
                        </Button>

                        {submitError && (
                            <Text sx={styles.cardError}>{submitError}</Text>
                        )}
                    </div>

                    <div>
                        <Button
                            id="submit-button"
                            sizeVariant="small"
                            sx={{
                                display: 'block',
                                width: '100%',
                                fontSize: 1,
                                py: [2, 3],
                                variant: 'forms.identity.verifyPassword.submit',
                            }}
                            type="submit"
                            isDisabled={isSubmitting || isRedirecting}
                        >
                            Verify
                        </Button>
                    </div>
                </form>

                <Button
                    id="back-button"
                    variant="outlined"
                    sizeVariant="small"
                    sx={{
                        display: 'block',
                        width: '100%',
                        fontSize: 1,
                        py: [2, 3],
                        textAlign: 'center',
                        variant: 'forms.identity.verifyPassword.back',
                    }}
                    type="button"
                    isDisabled={isSubmitting || isRedirecting}
                    href={`/login/verifypassword/cancel?contextId=${props.contextId}`}
                >
                    Back to login
                </Button>

                <Button
                    id="forgot-password-button"
                    sizeVariant="small"
                    variant="text"
                    onPress={onForgotPassword}
                    sx={styles.cardForgotPasswordLink}
                    isDisabled={isRedirecting}
                >
                    Forgot your password?
                </Button>
            </div>

            <UnderLinks {...props} />
        </Card>
    );
};

export default VerifyPasswordPage;
